/**
 * Shape validation for inbound client messages.
 *
 * The Gateway runs these on every message before anything else touches it
 * (docs/architecture/system-overview.md "Security Boundaries"). Semantic
 * validation (token validity, ownership, cooldowns, bounds) belongs to the
 * owning module — this layer only guarantees the message is well-formed and
 * within sane size/range limits.
 */
import { HINT_IDS, PROTOCOL_VERSION } from './messages.js';
export const MAX_CLIENT_MESSAGE_BYTES = 4096;
export const MAX_TOKEN_LENGTH = 200;
export const MAX_CONTENT_HASH_LENGTH = 128;
/** Rule 3 capability negotiation: at most this many names in `clientCapabilities`… */
export const MAX_CLIENT_CAPABILITIES = 16;
/** …each a non-empty string of at most this many characters. */
export const MAX_CAPABILITY_LENGTH = 32;
export const MAX_ITEM_ID_LENGTH = 64;
export const MAX_CONTENT_ID_LENGTH = 64;
export const MAX_CRAFT_INPUTS = 16;
export const MAX_TRADE_ITEMS = 16;
export const MAX_TRADE_RESOURCES = 16;
export const MAX_ABILITY_ID_LENGTH = 64;
export const MAX_LOADOUT_SIZE = 4;
// Social (P8.2, social-systems.md §3.3/§4.1). A character name is 3-24 chars
// (NAME_PATTERN, httpApi); the wire bound here is a loose upper limit (the
// Social module resolves names to accounts). Chat bodies are plain text, capped
// at the §3.3 length; report notes are a short bounded note.
export const MAX_CHAT_BODY_LENGTH = 256;
export const MAX_CHAR_NAME_LENGTH = 24;
export const MAX_REPORT_NOTE_LENGTH = 280;
const CHAT_CHANNELS = new Set(['zone', 'party', 'guild', 'whisper', 'global']);
const REPORT_REASONS = new Set(['harassment', 'spam', 'hate', 'scam', 'other']);
// Bug reports (`q-bug-report-button`). Sized so a full report stays well under
// MAX_CLIENT_MESSAGE_BYTES: note 500 + 12 log lines × 160 + small fields ≈ 2.6 KB of
// characters; the client trims the log tail further if escaping would push it over.
export const MAX_BUG_REPORT_NOTE_LENGTH = 500;
export const MAX_BUG_REPORT_LOG_LINES = 12;
export const MAX_BUG_REPORT_LOG_LINE_LENGTH = 160;
export const MAX_BUG_REPORT_CLIENT_BUILD_LENGTH = 64;
const BUG_REPORT_RENDERERS = new Set(['webgl2', 'webgpu']);
const BUG_REPORT_LAYOUTS = new Set(['desktop', 'portrait', 'landscape']);
/**
 * The conduct actions a moderator may request over the wire (SEC-6 §4.3/§4.6).
 * CLOSED on purpose: account-data deletion is Owner-Reserved and is not a
 * moderator action, and no economic action is reachable from this surface at all.
 */
const MODERATION_ACTIONS = new Set(['warn', 'mute', 'kick', 'suspend', 'ban']);
/** Justification/dismissal-reason length. Same order as a report note. */
export const MAX_MODERATION_REASON_LENGTH = 280;
/**
 * Longest timed mute/suspend a single in-game command may set: 365 days. Not a
 * policy about sentence length — an indefinite ban is already available as its
 * own action, so a duration beyond a year expresses nothing the ladder cannot,
 * and an unbounded number here is a way to overflow a date at the far end.
 */
export const MAX_MODERATION_DURATION_MS = 365 * 24 * 60 * 60 * 1000;
/** Hard ceiling on a requested queue page (the server caps again, independently). */
export const MAX_MOD_QUEUE_LIMIT = 20;
/**
 * Appeals (SEC-6 §6, §13 Phase B). The statement is the player's own account of
 * their behaviour, so it is bounded generously — a bound tight enough to truncate
 * a defence is the wire-level version of §6.3's "may not require the player to
 * prove innocence". Same order as a mail body, which is the longest free text a
 * player already writes.
 */
export const MAX_APPEAL_STATEMENT_LENGTH = 2000;
/**
 * The verdicts a reviewer may return (SEC-6 §6.2 item 4). CLOSED, and `open` is
 * deliberately absent: it is a state, never a verdict, and accepting it here
 * would let a decision message quietly un-decide an appeal.
 */
const APPEAL_VERDICTS = new Set(['upheld', 'overturned', 'modified']);
// Mail (P8.3, social-systems.md §5). Subject/body are bounded plain text; the
// per-mail item-attachment count is a loose wire bound (the service enforces the
// §5.5 caps + the A1 gate). Shard/COD amounts are non-negative integers.
export const MAX_MAIL_SUBJECT_LENGTH = 128;
export const MAX_MAIL_BODY_LENGTH = 2000;
export const MAX_MAIL_ATTACHMENTS = 16;
// Guilds (P8.4, social-systems.md §6.1). Name + short tag are bounded plain text
// (uniqueness + semantics are enforced by the GuildService); the motd is a short
// bounded plain-text notice. These are loose wire upper bounds.
export const MAX_GUILD_NAME_LENGTH = 32;
export const MAX_GUILD_TAG_LENGTH = 5;
export const MAX_GUILD_MOTD_LENGTH = 256;
// Housing (P8.5, social-systems.md §7). Bounded plain-text house name; bounded
// slot/transform handle + entitlement/template ids; a sane upper bound on the
// allow/ban list (semantics — ownership, caps, the fee — are the HouseService's).
export const MAX_HOUSE_NAME_LENGTH = 48;
export const MAX_HOUSE_SLOT_LENGTH = 64;
export const MAX_HOUSE_VISITOR_LIST = 200;
// Workshops (P8.6, social-systems.md §8). A loose wire upper bound on the batch
// size (the real per-house active-job cap + the super-linear upkeep are the
// WorkshopService's semantic checks, §8.3); inputs reuse the craft-input bound.
export const MAX_WORKSHOP_BATCH = 1000;
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isFiniteNumber(v) {
    return typeof v === 'number' && Number.isFinite(v);
}
function isNonNegativeInt(v) {
    return isFiniteNumber(v) && Number.isInteger(v) && v >= 0 && v <= Number.MAX_SAFE_INTEGER;
}
function isPositiveInt(v) {
    return isNonNegativeInt(v) && v > 0;
}
function isBoundedString(v, maxLen) {
    return typeof v === 'string' && v.length > 0 && v.length <= maxLen;
}
/** Declare the exact key set of a wire record type (checked both ways by `tsc`). */
function keysOf(map) {
    return Object.freeze(Object.keys(map));
}
/** True when every own key of `d` is one of `keys` (strict shape). */
function hasOnlyKeys(d, keys) {
    for (const k of Object.keys(d)) {
        if (!keys.includes(k))
            return false;
    }
    return true;
}
/** Nested wire records that are read inside a payload (strict like the payload). */
const CRAFT_INPUT_KEYS = keysOf({ resource: true, quality: true, quantity: true });
const TRADE_RESOURCE_KEYS = keysOf({
    resourceId: true,
    quality: true,
    quantity: true,
});
const WORKSHOP_INPUT_KEYS = keysOf({
    resource: true,
    quality: true,
    quantity: true,
});
// --- Pagination ceilings at the wire (SEC-3 §3.1 A-11 D-6) --------------------
//
// A bound the validator REFUSES, not a value it rewrites — the
// `MAX_MOD_QUEUE_LIMIT` precedent below: the server keeps its own clamp as
// defence in depth, but an unbounded `offset` reaching a query is a
// database-cost amplifier (`OFFSET n` walks n rows for anyone who asks), so the
// ceiling lives HERE, at the single inbound gate. Values are the server's own
// ceilings (gateway/{market,auction,stall}.ts `MAX_SEARCH_LIMIT` 200 · price
// history 365 d · arena `leaderboardLimit` 50), so no request the server would
// have honoured in full is refused; the offset ceiling is new — a DB-cost bound,
// not a UX limit (no client pages anywhere near it).
export const MAX_PAGE_LIMIT = 200;
export const MAX_PAGE_OFFSET = 10_000;
export const MAX_PRICE_HISTORY_DAYS = 365;
export const MAX_LEADERBOARD_LIMIT = 50;
function isPageLimit(v) {
    return isNonNegativeInt(v) && v <= MAX_PAGE_LIMIT;
}
function isPageOffset(v) {
    return isNonNegativeInt(v) && v <= MAX_PAGE_OFFSET;
}
function validateHello(d) {
    return (d['protocolVersion'] === PROTOCOL_VERSION &&
        isBoundedString(d['token'], MAX_TOKEN_LENGTH) &&
        isPositiveInt(d['characterId']) &&
        typeof d['contentHash'] === 'string' &&
        d['contentHash'].length <= MAX_CONTENT_HASH_LENGTH &&
        isOptional(d['takeover'], (v) => typeof v === 'boolean') &&
        isOptional(d['snapshotHz'], isRequestedSnapshotHz) &&
        isOptional(d['clientCapabilities'], isCapabilityList));
}
/**
 * A client's capability list (protocol-versioning.md Rule 3): an array of at most
 * MAX_CLIENT_CAPABILITIES non-empty strings of at most MAX_CAPABILITY_LENGTH characters.
 * Names this build does not know are allowed (a newer client may list more); the server uses
 * only those it implements.
 */
function isCapabilityList(v) {
    return (Array.isArray(v) &&
        v.length <= MAX_CLIENT_CAPABILITIES &&
        v.every((c) => isBoundedString(c, MAX_CAPABILITY_LENGTH)));
}
/**
 * A requested snapshot rate (D1): any finite number in (0, 60]. Deliberately wider than
 * SNAPSHOT_HZ_CHOICES — the SERVER decides (a non-choice falls back to its default), so a
 * later client asking for a rate this build does not run is served, not refused.
 */
function isRequestedSnapshotHz(v) {
    return isFiniteNumber(v) && v > 0 && v <= 60;
}
function validateResume(d) {
    return (d['protocolVersion'] === PROTOCOL_VERSION &&
        isBoundedString(d['token'], MAX_TOKEN_LENGTH) &&
        isPositiveInt(d['characterId']) &&
        isNonNegativeInt(d['lastSeq']) &&
        isOptional(d['snapshotHz'], isRequestedSnapshotHz) &&
        isOptional(d['clientCapabilities'], isCapabilityList));
}
function validateInput(d) {
    // Move components must sit in the unit square; magnitude is re-clamped by
    // the shared movement code, so a slightly long diagonal is acceptable here.
    return (isFiniteNumber(d['moveX']) &&
        Math.abs(d['moveX']) <= 1.000001 &&
        isFiniteNumber(d['moveZ']) &&
        Math.abs(d['moveZ']) <= 1.000001 &&
        isFiniteNumber(d['facing']) &&
        Math.abs(d['facing']) <= 16 &&
        typeof d['dodge'] === 'boolean');
}
function validatePing(d) {
    return isFiniteNumber(d['t0']) && d['t0'] >= 0;
}
function validateInteractIntent(d) {
    // Node ids are positive integers (zone node-id namespace starts at 1).
    return isPositiveInt(d['nodeId']);
}
function validateEquipIntent(d) {
    // Item ids are UUIDs; the shape layer just bounds them. Ownership and
    // existence are the Economy/Persistence layer's checks.
    return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH);
}
function validateUnequipIntent(d) {
    return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH);
}
function validateItemUseIntent(d) {
    // Shape only; ownership, location and the item's `use` are the server's checks.
    return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH);
}
function validateCraftIntent(d) {
    if (!isBoundedString(d['recipeId'], MAX_CONTENT_ID_LENGTH))
        return false;
    const inputs = d['inputs'];
    if (!Array.isArray(inputs) || inputs.length < 1 || inputs.length > MAX_CRAFT_INPUTS) {
        return false;
    }
    for (const input of inputs) {
        if (!isRecord(input) || !hasOnlyKeys(input, CRAFT_INPUT_KEYS))
            return false;
        if (!isBoundedString(input['resource'], MAX_CONTENT_ID_LENGTH))
            return false;
        const quality = input['quality'];
        if (!isPositiveInt(quality) || quality > 5)
            return false;
        if (!isPositiveInt(input['quantity']))
            return false;
    }
    return true;
}
function validateChooseSpecialisation(d) {
    return (isBoundedString(d['discipline'], MAX_CONTENT_ID_LENGTH) &&
        isBoundedString(d['specialisation'], MAX_CONTENT_ID_LENGTH));
}
function validateTalkIntent(d) {
    return isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH);
}
function validateQuestAccept(d) {
    return isBoundedString(d['questId'], MAX_CONTENT_ID_LENGTH);
}
function validateQuestTurnIn(d) {
    return isBoundedString(d['questId'], MAX_CONTENT_ID_LENGTH);
}
function validateDeliverIntent(d) {
    return (isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH) &&
        isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH));
}
function validateTradeResourceLeg(v) {
    if (!isRecord(v) || !hasOnlyKeys(v, TRADE_RESOURCE_KEYS))
        return false;
    if (!isBoundedString(v['resourceId'], MAX_CONTENT_ID_LENGTH))
        return false;
    const quality = v['quality'];
    if (!isPositiveInt(quality) || quality > 5)
        return false;
    return isPositiveInt(v['quantity']);
}
/** Shared shape check for a full trade offer (item ids + resource legs + Shards). */
function isTradeOfferShape(d) {
    const itemIds = d['itemIds'];
    if (!Array.isArray(itemIds) || itemIds.length > MAX_TRADE_ITEMS)
        return false;
    for (const id of itemIds) {
        if (!isBoundedString(id, MAX_ITEM_ID_LENGTH))
            return false;
    }
    const resources = d['resources'];
    if (!Array.isArray(resources) || resources.length > MAX_TRADE_RESOURCES)
        return false;
    for (const leg of resources) {
        if (!validateTradeResourceLeg(leg))
            return false;
    }
    return isNonNegativeInt(d['shards']);
}
function validateTradeRequest(d) {
    // Entity ids are positive integers (the zone entity-id namespace starts at 1).
    return isPositiveInt(d['targetEntityId']);
}
function validateTradeRespond(d) {
    return typeof d['accept'] === 'boolean';
}
function validateTradeOffer(d) {
    return isTradeOfferShape(d);
}
function validateTradeConfirm(d) {
    return isNonNegativeInt(d['revision']);
}
function validateTradeSubmit(d) {
    return isNonNegativeInt(d['revision']);
}
/** An optional field is valid when absent, or present and passing `check`. */
function isOptional(v, check) {
    return v === undefined || check(v);
}
function validateMarketSearch(d) {
    // Every field is optional (omitted = unfiltered). Prices are non-negative
    // Shard amounts; limit/offset are non-negative paging integers bounded HERE
    // (D-6; the server re-clamps as defence in depth). contentId, when present,
    // is a bounded content id.
    return (isOptional(d['contentId'], (x) => isBoundedString(x, MAX_CONTENT_ID_LENGTH)) &&
        isOptional(d['minPrice'], isNonNegativeInt) &&
        isOptional(d['maxPrice'], isNonNegativeInt) &&
        isOptional(d['limit'], isPageLimit) &&
        isOptional(d['offset'], isPageOffset));
}
function validateMarketList(d) {
    // Ask price is a positive Shard amount; the listing fee is computed server-side.
    if (!isPositiveInt(d['askPrice']))
        return false;
    // Exactly one payload form: an item instance id XOR a resource stack. The
    // economy layer re-checks ownership/tradeability; here we only fix the shape.
    const hasItem = d['itemId'] !== undefined;
    const hasResource = d['resource'] !== undefined;
    if (hasItem === hasResource)
        return false;
    if (hasItem)
        return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH);
    return validateTradeResourceLeg(d['resource']);
}
function validateMarketBuy(d) {
    // Listing ids are positive integers (the bigint identity column starts at 1).
    return isPositiveInt(d['listingId']);
}
function validateMarketCancel(d) {
    return isPositiveInt(d['listingId']);
}
function validatePriceHistoryRequest(d) {
    // contentId is required; days is an optional positive window, bounded here
    // (D-6; the server clamps to the same 365 d independently).
    return (isBoundedString(d['contentId'], MAX_CONTENT_ID_LENGTH) &&
        isOptional(d['days'], (x) => isPositiveInt(x) && x <= MAX_PRICE_HISTORY_DAYS));
}
// --- Auctions (P5.3) ---
function validateAuctionSearch(d) {
    // Every field optional (omitted = unfiltered), like marketSearch but no price range.
    return (isOptional(d['contentId'], (x) => isBoundedString(x, MAX_CONTENT_ID_LENGTH)) &&
        isOptional(d['limit'], isPageLimit) &&
        isOptional(d['offset'], isPageOffset));
}
function validateAuctionCreate(d) {
    // Item-only (no resource form); minBid is a positive Shard amount. Eligibility
    // (notable item, tradeable, ownership) is the economy layer's check.
    return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH) && isPositiveInt(d['minBid']);
}
function validateAuctionBid(d) {
    // Auction ids are positive bigint identities; bidAmount is a positive Shard
    // amount (the >= minimum / must-exceed rule is enforced server-side).
    return isPositiveInt(d['auctionId']) && isPositiveInt(d['bidAmount']);
}
// --- Local stalls (P5.3) ---
function validateStallSearch(d) {
    return (isOptional(d['contentId'], (x) => isBoundedString(x, MAX_CONTENT_ID_LENGTH)) &&
        isOptional(d['minPrice'], isNonNegativeInt) &&
        isOptional(d['maxPrice'], isNonNegativeInt) &&
        isOptional(d['limit'], isPageLimit) &&
        isOptional(d['offset'], isPageOffset));
}
function validateStallOpen(d) {
    // Ask price is a positive Shard amount; rent is computed server-side. Exactly
    // one payload form: an item instance id XOR a resource stack (like marketList).
    // An optional cosmetic skin id may accompany either (presentation only).
    if (!isPositiveInt(d['askPrice']))
        return false;
    if (!isOptional(d['skinContentId'], (x) => isBoundedString(x, MAX_CONTENT_ID_LENGTH))) {
        return false;
    }
    const hasItem = d['itemId'] !== undefined;
    const hasResource = d['resource'] !== undefined;
    if (hasItem === hasResource)
        return false;
    if (hasItem)
        return isBoundedString(d['itemId'], MAX_ITEM_ID_LENGTH);
    return validateTradeResourceLeg(d['resource']);
}
function validateStallBuy(d) {
    return isPositiveInt(d['stallId']);
}
function validateStallClose(d) {
    return isPositiveInt(d['stallId']);
}
// --- Abilities (P7.2) ---
function validateAbilityIntent(d) {
    if (!isBoundedString(d['abilityId'], MAX_ABILITY_ID_LENGTH))
        return false;
    return (isOptional(d['targetEntityId'], isPositiveInt) &&
        isOptional(d['targetX'], isFiniteNumber) &&
        isOptional(d['targetZ'], isFiniteNumber));
}
function validateLoadoutChange(d) {
    const chosen = d['chosenAbilities'];
    if (!Array.isArray(chosen) || chosen.length > MAX_LOADOUT_SIZE)
        return false;
    for (const id of chosen) {
        if (!isBoundedString(id, MAX_ABILITY_ID_LENGTH))
            return false;
    }
    return true;
}
// --- Social: chat, friends, presence, moderation (P8.2) ---
function validateChatSend(d) {
    // Channel is one of the fixed set; body is non-empty plain text within the
    // length cap; `to` is an optional whisper-target name. Semantic checks (rate,
    // membership, mute/block) belong to the Social module.
    if (typeof d['channel'] !== 'string' || !CHAT_CHANNELS.has(d['channel']))
        return false;
    if (!isBoundedString(d['body'], MAX_CHAT_BODY_LENGTH))
        return false;
    return isOptional(d['to'], (x) => isBoundedString(x, MAX_CHAR_NAME_LENGTH));
}
function validateFriendRequest(d) {
    return isBoundedString(d['name'], MAX_CHAR_NAME_LENGTH);
}
function validateFriendRespond(d) {
    return isPositiveInt(d['fromAccountId']) && typeof d['accept'] === 'boolean';
}
function validateFriendRemove(d) {
    return isPositiveInt(d['accountId']);
}
function validateJoinFriend(d) {
    return isPositiveInt(d['accountId']);
}
function validatePresenceSet(d) {
    return typeof d['appearOffline'] === 'boolean';
}
function validateMuteSet(d) {
    return isPositiveInt(d['accountId']) && typeof d['muted'] === 'boolean';
}
function validateBlockSet(d) {
    return isPositiveInt(d['accountId']) && typeof d['blocked'] === 'boolean';
}
function validateModQueue(d) {
    return isOptional(d['limit'], (x) => isPositiveInt(x) && x <= MAX_MOD_QUEUE_LIMIT);
}
function validateModAct(d) {
    if (!isBoundedString(d['name'], MAX_CHAR_NAME_LENGTH))
        return false;
    if (typeof d['action'] !== 'string' || !MODERATION_ACTIONS.has(d['action']))
        return false;
    if (!isOptional(d['reason'], (x) => isBoundedString(x, MAX_MODERATION_REASON_LENGTH))) {
        return false;
    }
    if (!isOptional(d['reportId'], isPositiveInt))
        return false;
    return isOptional(d['durationMs'], (x) => isPositiveInt(x) && x <= MAX_MODERATION_DURATION_MS);
}
function validateModLift(d) {
    if (!isPositiveInt(d['actionId']))
        return false;
    return isOptional(d['reason'], (x) => isBoundedString(x, MAX_MODERATION_REASON_LENGTH));
}
function validateModDismiss(d) {
    if (!isPositiveInt(d['reportId']))
        return false;
    // REQUIRED, not optional — a dismissal is a decision (SEC-6 §4.5). The service
    // refuses a whitespace-only reason too; this bounds the shape, that bounds the
    // meaning.
    return isBoundedString(d['reason'], MAX_MODERATION_REASON_LENGTH);
}
// --- Appeals (SEC-6 §6, §13 Phase B) ---
function validateAppealFile(d) {
    if (!isPositiveInt(d['actionId']))
        return false;
    // REQUIRED and non-empty at the wire, as `modDismiss`'s reason is: an appeal
    // with no statement is a row that says a player objected and nothing a reviewer
    // can act on. The service refuses a whitespace-only statement too.
    return isBoundedString(d['statement'], MAX_APPEAL_STATEMENT_LENGTH);
}
function validateModAppeals(d) {
    // Same page bound as the report queue rather than a second constant meaning the
    // same thing; the service caps independently at its own ceiling.
    return isOptional(d['limit'], (x) => isPositiveInt(x) && x <= MAX_MOD_QUEUE_LIMIT);
}
function validateModAppealDecide(d) {
    if (!isPositiveInt(d['appealId']))
        return false;
    if (typeof d['verdict'] !== 'string' || !APPEAL_VERDICTS.has(d['verdict']))
        return false;
    // REQUIRED: §6.3 forbids an appeal being silently dropped, and a verdict with
    // no stated reason is the quiet version of that.
    return isBoundedString(d['reason'], MAX_MODERATION_REASON_LENGTH);
}
function validateReport(d) {
    if (!isBoundedString(d['name'], MAX_CHAR_NAME_LENGTH))
        return false;
    if (typeof d['reason'] !== 'string' || !REPORT_REASONS.has(d['reason']))
        return false;
    if (!isOptional(d['note'], (x) => isBoundedString(x, MAX_REPORT_NOTE_LENGTH)))
        return false;
    return isOptional(d['channel'], (x) => typeof x === 'string' && CHAT_CHANNELS.has(x));
}
function validateBugReport(d) {
    if (!isOptional(d['note'], (x) => isBoundedString(x, MAX_BUG_REPORT_NOTE_LENGTH)))
        return false;
    if (!isOptional(d['clientLog'], (x) => Array.isArray(x) &&
        x.length <= MAX_BUG_REPORT_LOG_LINES &&
        x.every((l) => isBoundedString(l, MAX_BUG_REPORT_LOG_LINE_LENGTH)))) {
        return false;
    }
    if (!isOptional(d['renderer'], (x) => typeof x === 'string' && BUG_REPORT_RENDERERS.has(x))) {
        return false;
    }
    if (!isOptional(d['layout'], (x) => typeof x === 'string' && BUG_REPORT_LAYOUTS.has(x))) {
        return false;
    }
    return isOptional(d['clientBuild'], (x) => isBoundedString(x, MAX_BUG_REPORT_CLIENT_BUILD_LENGTH));
}
// --- Mail (P8.3, social-systems.md §5) ---
function validateMailSend(d) {
    if (!isBoundedString(d['to'], MAX_CHAR_NAME_LENGTH))
        return false;
    if (!isBoundedString(d['subject'], MAX_MAIL_SUBJECT_LENGTH))
        return false;
    if (!isBoundedString(d['body'], MAX_MAIL_BODY_LENGTH))
        return false;
    if (!isOptional(d['itemInstanceIds'], (x) => {
        if (!Array.isArray(x) || x.length > MAX_MAIL_ATTACHMENTS)
            return false;
        return x.every((id) => isBoundedString(id, MAX_ITEM_ID_LENGTH));
    })) {
        return false;
    }
    if (!isOptional(d['shards'], isNonNegativeInt))
        return false;
    return isOptional(d['codAmount'], isNonNegativeInt);
}
function validateMailClaim(d) {
    return isPositiveInt(d['mailId']);
}
function validateMailReturn(d) {
    return isPositiveInt(d['mailId']);
}
// --- Guilds (P8.4, social-systems.md §6) ---
function validateGuildCreate(d) {
    // Non-empty bounded name + tag. Uniqueness + the founding fee + the A1 gate are
    // semantic checks the GuildService owns.
    return (isBoundedString(d['name'], MAX_GUILD_NAME_LENGTH) &&
        isBoundedString(d['tag'], MAX_GUILD_TAG_LENGTH));
}
function validateGuildInvite(d) {
    return isBoundedString(d['name'], MAX_CHAR_NAME_LENGTH);
}
function validateGuildInviteRespond(d) {
    return isPositiveInt(d['guildId']) && typeof d['accept'] === 'boolean';
}
function validateGuildKick(d) {
    return isPositiveInt(d['accountId']);
}
function validateGuildPromote(d) {
    return isPositiveInt(d['accountId']);
}
function validateGuildDemote(d) {
    return isPositiveInt(d['accountId']);
}
function validateGuildSetMotd(d) {
    // The motd may be cleared to empty, so allow a 0-length string up to the cap.
    return typeof d['motd'] === 'string' && d['motd'].length <= MAX_GUILD_MOTD_LENGTH;
}
function validateGuildTransfer(d) {
    return isPositiveInt(d['accountId']);
}
// --- Housing (P8.5, §7) -----------------------------------------------------
/** A bounded array of positive-int account ids (the allow/ban lists). */
function isAccountIdArray(v) {
    return Array.isArray(v) && v.length <= MAX_HOUSE_VISITOR_LIST && v.every((x) => isPositiveInt(x));
}
function isHousePermissionPolicy(v) {
    return v === 'private' || v === 'friends' || v === 'guild' || v === 'public';
}
function validateHouseAcquire(d) {
    // Non-empty bounded template id; optional bounded name. The template must exist,
    // the A1 gate, and the fee are semantic checks the HouseService owns.
    return (isBoundedString(d['templateId'], MAX_CONTENT_ID_LENGTH) &&
        isOptional(d['name'], (x) => isBoundedString(x, MAX_HOUSE_NAME_LENGTH)));
}
function validateHouseEnter(d) {
    return isPositiveInt(d['houseId']);
}
function validateHousePlaceDecoration(d) {
    return (isPositiveInt(d['houseId']) &&
        isBoundedString(d['itemInstanceId'], MAX_ITEM_ID_LENGTH) &&
        isBoundedString(d['slot'], MAX_HOUSE_SLOT_LENGTH));
}
function validateHouseRemoveDecoration(d) {
    return isPositiveInt(d['houseId']) && isPositiveInt(d['decorationId']);
}
function validateHousePlaceTrophy(d) {
    return (isPositiveInt(d['houseId']) &&
        isBoundedString(d['entitlementId'], MAX_CONTENT_ID_LENGTH) &&
        isBoundedString(d['slot'], MAX_HOUSE_SLOT_LENGTH));
}
function validateHouseRemoveTrophy(d) {
    return isPositiveInt(d['houseId']) && isPositiveInt(d['decorationId']);
}
function validateHouseSetPermissions(d) {
    return (isPositiveInt(d['houseId']) &&
        isHousePermissionPolicy(d['policy']) &&
        isOptional(d['allow'], isAccountIdArray) &&
        isOptional(d['ban'], isAccountIdArray));
}
function validateHouseSetName(d) {
    return isPositiveInt(d['houseId']) && isBoundedString(d['name'], MAX_HOUSE_NAME_LENGTH);
}
function validateHousePayUpkeep(d) {
    return isPositiveInt(d['houseId']);
}
// --- Workshops (P8.6, §8) ---------------------------------------------------
function validateWorkshopQueueJob(d) {
    // houseId + recipeId + a non-empty bounded inputs array (same shape as craft) +
    // a bounded batch size. Ownership, dormancy, the recipe, the queue cap and the
    // super-linear upkeep are semantic checks the WorkshopService owns (§8.3/§8.5).
    if (!isPositiveInt(d['houseId']))
        return false;
    if (!isBoundedString(d['recipeId'], MAX_CONTENT_ID_LENGTH))
        return false;
    const quantity = d['quantity'];
    if (!isPositiveInt(quantity) || quantity > MAX_WORKSHOP_BATCH)
        return false;
    const inputs = d['inputs'];
    if (!Array.isArray(inputs) || inputs.length < 1 || inputs.length > MAX_CRAFT_INPUTS) {
        return false;
    }
    for (const input of inputs) {
        if (!isRecord(input) || !hasOnlyKeys(input, WORKSHOP_INPUT_KEYS))
            return false;
        if (!isBoundedString(input['resource'], MAX_CONTENT_ID_LENGTH))
            return false;
        const q = input['quality'];
        if (!isPositiveInt(q) || q > 5)
            return false;
        if (!isPositiveInt(input['quantity']))
            return false;
    }
    return true;
}
function validateWorkshopCollect(d) {
    return isPositiveInt(d['jobId']);
}
function validateWorkshopCancel(d) {
    return isPositiveInt(d['jobId']);
}
/** Arena format ids (P9.2, §3.3). The format must be one of these; the rest is semantic. */
const ARENA_FORMAT_IDS = ['duel', 'skirmish', 'team'];
function validateArenaQueue(d) {
    return typeof d['format'] === 'string' && ARENA_FORMAT_IDS.includes(d['format']);
}
function validateArenaLeaderboard(d) {
    // `limit` is bounded here (D-6; the server caps at its own `leaderboardLimit`).
    return (typeof d['format'] === 'string' &&
        ARENA_FORMAT_IDS.includes(d['format']) &&
        isOptional(d['limit'], (x) => isPositiveInt(x) && x <= MAX_LEADERBOARD_LIMIT));
}
/** Max raid roster size on the wire — a bound, the encounter band is the semantic cap (§5.2). */
const MAX_RAID_ROSTER = 40;
/** Validate a raidForm frame (P9.3, §5.3): a bounded encounter id + an optional roster of account ids. */
function validateRaidForm(d) {
    if (!isBoundedString(d['encounterId'], 128))
        return false;
    const members = d['members'];
    if (members === undefined)
        return true;
    return (Array.isArray(members) &&
        members.length <= MAX_RAID_ROSTER &&
        members.every((m) => isPositiveInt(m)));
}
/** Validate a raidInvite frame (SEC-3 §8 item 6, AD-402): a bounded encounter id + one account id. */
function validateRaidInvite(d) {
    return isBoundedString(d['encounterId'], 128) && isPositiveInt(d['memberAccountId']);
}
/** Validate a raidAccept frame (AD-402): the invitation id the invitee is answering. */
function validateRaidAccept(d) {
    return isPositiveInt(d['invitationId']);
}
/** Validate a vendorList frame (AD-403): the NPC whose shelf is asked for — same bound as talkIntent. */
function validateVendorList(d) {
    return isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH);
}
/** Validate a repair frame (AD-672 S2): the vendor NPC (talkIntent bound) + one owned item instance id. */
function validateRepair(d) {
    return (isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH) &&
        isBoundedString(d['itemInstanceId'], MAX_ITEM_ID_LENGTH));
}
/** Validate a vendorSell frame (AD-672 S3): the vendor NPC (talkIntent bound) + one owned item instance id. */
function validateVendorSell(d) {
    return (isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH) &&
        isBoundedString(d['itemInstanceId'], MAX_ITEM_ID_LENGTH));
}
/** Validate a travel frame (AD-672 S4): the destination shrine id (content-id bound). */
function validateTravel(d) {
    return isBoundedString(d['shrineId'], MAX_CONTENT_ID_LENGTH);
}
// The five SP-5 caps, spelled here rather than imported so the client bundle
// takes no capacity code (the client words the server's reading; it never
// computes a cap). Both directions are compile-checked against `CapacityKey`.
const CAPACITY_UPGRADE_CAPS = [
    'purse',
    'sack',
    'pack',
    'chest',
    'account',
];
const _capacityCapsExhaustive = true;
void _capacityCapsExhaustive;
/**
 * Validate a capacityUpgrade frame (AD-775 §6.1): the vendor NPC (talkIntent
 * bound), one of the five SP-5 caps, and the confirmed tier (a non-negative
 * integer; the server judges it against the holder's real tier), and an
 * optional container item id to bind (§6.2).
 */
function validateCapacityUpgrade(d) {
    const tier = d['tier'];
    return (isBoundedString(d['npcId'], MAX_CONTENT_ID_LENGTH) &&
        CAPACITY_UPGRADE_CAPS.includes(d['cap']) &&
        typeof tier === 'number' &&
        Number.isInteger(tier) &&
        tier >= 0 &&
        tier <= 1_000 &&
        isOptional(d['containerId'], (v) => isBoundedString(v, MAX_ITEM_ID_LENGTH)));
}
/** Validate a dungeonEnter frame (E4.6.S3 slice 2c): the site id only (content-id bound). */
function validateDungeonEnter(d) {
    return isBoundedString(d['dungeonId'], MAX_CONTENT_ID_LENGTH);
}
function validateHintEvent(d) {
    return (typeof d['hintId'] === 'string' &&
        HINT_IDS.includes(d['hintId']) &&
        (d['event'] === 'shown' || d['event'] === 'acted'));
}
/** Field check for a payload-less message (`Record<string, never>`): the exact-key test is the whole test. */
const ACCEPT = () => true;
/**
 * The shape table — ONE entry per `ClientMessageType` member, `tsc`-enforced
 * (`Record<ClientMessageType, …>` fails to compile on a missing member) and
 * re-checked at runtime against messages.ts source by shape-enumeration.test.ts
 * (SEC-3 D-7). `keys` is the exact payload key set (D-5); `check` is the field
 * validator; `error` the reject code a failed check returns.
 */
export const CLIENT_MESSAGE_SHAPES = {
    hello: {
        keys: keysOf({
            protocolVersion: true,
            token: true,
            characterId: true,
            contentHash: true,
            takeover: true,
            snapshotHz: true,
            clientCapabilities: true,
        }),
        check: validateHello,
        error: 'badHello',
    },
    resume: {
        keys: keysOf({
            protocolVersion: true,
            token: true,
            characterId: true,
            lastSeq: true,
            snapshotHz: true,
            clientCapabilities: true,
        }),
        check: validateResume,
        error: 'badResume',
    },
    input: {
        keys: keysOf({ moveX: true, moveZ: true, facing: true, dodge: true }),
        check: validateInput,
        error: 'badInput',
    },
    ping: { keys: keysOf({ t0: true }), check: validatePing, error: 'badPing' },
    interactIntent: {
        keys: keysOf({ nodeId: true }),
        check: validateInteractIntent,
        error: 'badInteractIntent',
    },
    inventoryRequest: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badInventoryRequest',
    },
    walletRequest: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badWalletRequest',
    },
    equipIntent: {
        keys: keysOf({ itemId: true }),
        check: validateEquipIntent,
        error: 'badEquipIntent',
    },
    unequipIntent: {
        keys: keysOf({ itemId: true }),
        check: validateUnequipIntent,
        error: 'badUnequipIntent',
    },
    itemUseIntent: {
        keys: keysOf({ itemId: true }),
        check: validateItemUseIntent,
        error: 'badItemUseIntent',
    },
    craftIntent: {
        keys: keysOf({ recipeId: true, inputs: true }),
        check: validateCraftIntent,
        error: 'badCraftIntent',
    },
    attackIntent: { keys: keysOf({}), check: ACCEPT, error: 'badAttackIntent' },
    abilityIntent: {
        keys: keysOf({
            abilityId: true,
            targetEntityId: true,
            targetX: true,
            targetZ: true,
        }),
        check: validateAbilityIntent,
        error: 'badAbilityIntent',
    },
    loadoutChange: {
        keys: keysOf({ chosenAbilities: true }),
        check: validateLoadoutChange,
        error: 'badLoadoutChange',
    },
    chooseSpecialisation: {
        keys: keysOf({ discipline: true, specialisation: true }),
        check: validateChooseSpecialisation,
        error: 'badChooseSpecialisation',
    },
    talkIntent: {
        keys: keysOf({ npcId: true }),
        check: validateTalkIntent,
        error: 'badTalkIntent',
    },
    questAccept: {
        keys: keysOf({ questId: true }),
        check: validateQuestAccept,
        error: 'badQuestAccept',
    },
    questTurnIn: {
        keys: keysOf({ questId: true }),
        check: validateQuestTurnIn,
        error: 'badQuestTurnIn',
    },
    deliverIntent: {
        keys: keysOf({ npcId: true, itemId: true }),
        check: validateDeliverIntent,
        error: 'badDeliverIntent',
    },
    tradeRequest: {
        keys: keysOf({ targetEntityId: true }),
        check: validateTradeRequest,
        error: 'badTradeRequest',
    },
    tradeRespond: {
        keys: keysOf({ accept: true }),
        check: validateTradeRespond,
        error: 'badTradeRespond',
    },
    tradeOffer: {
        keys: keysOf({ itemIds: true, resources: true, shards: true }),
        check: validateTradeOffer,
        error: 'badTradeOffer',
    },
    tradeSubmit: {
        keys: keysOf({ revision: true }),
        check: validateTradeSubmit,
        error: 'badTradeSubmit',
    },
    tradeConfirm: {
        keys: keysOf({ revision: true }),
        check: validateTradeConfirm,
        error: 'badTradeConfirm',
    },
    tradeUnsubmit: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badTradeUnsubmit',
    },
    tradeCancel: { keys: keysOf({}), check: ACCEPT, error: 'badTradeCancel' },
    marketSearch: {
        keys: keysOf({
            contentId: true,
            minPrice: true,
            maxPrice: true,
            limit: true,
            offset: true,
        }),
        check: validateMarketSearch,
        error: 'badMarketSearch',
    },
    marketList: {
        keys: keysOf({ itemId: true, resource: true, askPrice: true }),
        check: validateMarketList,
        error: 'badMarketList',
    },
    marketBuy: {
        keys: keysOf({ listingId: true }),
        check: validateMarketBuy,
        error: 'badMarketBuy',
    },
    marketCancel: {
        keys: keysOf({ listingId: true }),
        check: validateMarketCancel,
        error: 'badMarketCancel',
    },
    marketMine: { keys: keysOf({}), check: ACCEPT, error: 'badMarketMine' },
    priceHistoryRequest: {
        keys: keysOf({ contentId: true, days: true }),
        check: validatePriceHistoryRequest,
        error: 'badPriceHistoryRequest',
    },
    auctionSearch: {
        keys: keysOf({ contentId: true, limit: true, offset: true }),
        check: validateAuctionSearch,
        error: 'badAuctionSearch',
    },
    auctionCreate: {
        keys: keysOf({ itemId: true, minBid: true }),
        check: validateAuctionCreate,
        error: 'badAuctionCreate',
    },
    auctionBid: {
        keys: keysOf({ auctionId: true, bidAmount: true }),
        check: validateAuctionBid,
        error: 'badAuctionBid',
    },
    auctionMine: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badAuctionMine',
    },
    stallSearch: {
        keys: keysOf({
            contentId: true,
            minPrice: true,
            maxPrice: true,
            limit: true,
            offset: true,
        }),
        check: validateStallSearch,
        error: 'badStallSearch',
    },
    stallOpen: {
        keys: keysOf({
            itemId: true,
            resource: true,
            askPrice: true,
            skinContentId: true,
        }),
        check: validateStallOpen,
        error: 'badStallOpen',
    },
    stallBuy: {
        keys: keysOf({ stallId: true }),
        check: validateStallBuy,
        error: 'badStallBuy',
    },
    stallClose: {
        keys: keysOf({ stallId: true }),
        check: validateStallClose,
        error: 'badStallClose',
    },
    stallMine: { keys: keysOf({}), check: ACCEPT, error: 'badStallMine' },
    chatSend: {
        keys: keysOf({ channel: true, body: true, to: true }),
        check: validateChatSend,
        error: 'badChatSend',
    },
    friendRequest: {
        keys: keysOf({ name: true }),
        check: validateFriendRequest,
        error: 'badFriendRequest',
    },
    friendRespond: {
        keys: keysOf({ fromAccountId: true, accept: true }),
        check: validateFriendRespond,
        error: 'badFriendRespond',
    },
    friendRemove: {
        keys: keysOf({ accountId: true }),
        check: validateFriendRemove,
        error: 'badFriendRemove',
    },
    joinFriend: {
        keys: keysOf({ accountId: true }),
        check: validateJoinFriend,
        error: 'badJoinFriend',
    },
    friendsRequest: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badFriendsRequest',
    },
    presenceSet: {
        keys: keysOf({ appearOffline: true }),
        check: validatePresenceSet,
        error: 'badPresenceSet',
    },
    muteSet: {
        keys: keysOf({ accountId: true, muted: true }),
        check: validateMuteSet,
        error: 'badMuteSet',
    },
    blockSet: {
        keys: keysOf({ accountId: true, blocked: true }),
        check: validateBlockSet,
        error: 'badBlockSet',
    },
    report: {
        keys: keysOf({ name: true, reason: true, note: true, channel: true }),
        check: validateReport,
        error: 'badReport',
    },
    bugReport: {
        keys: keysOf({
            note: true,
            clientLog: true,
            renderer: true,
            layout: true,
            clientBuild: true,
        }),
        check: validateBugReport,
        error: 'badBugReport',
    },
    modQueue: {
        keys: keysOf({ limit: true }),
        check: validateModQueue,
        error: 'badModQueue',
    },
    modAct: {
        keys: keysOf({
            name: true,
            action: true,
            reason: true,
            reportId: true,
            durationMs: true,
        }),
        check: validateModAct,
        error: 'badModAct',
    },
    modLift: {
        keys: keysOf({ actionId: true, reason: true }),
        check: validateModLift,
        error: 'badModLift',
    },
    modDismiss: {
        keys: keysOf({ reportId: true, reason: true }),
        check: validateModDismiss,
        error: 'badModDismiss',
    },
    appealFile: {
        keys: keysOf({ actionId: true, statement: true }),
        check: validateAppealFile,
        error: 'badAppealFile',
    },
    modAppeals: {
        keys: keysOf({ limit: true }),
        check: validateModAppeals,
        error: 'badModAppeals',
    },
    modAppealDecide: {
        keys: keysOf({ appealId: true, verdict: true, reason: true }),
        check: validateModAppealDecide,
        error: 'badModAppealDecide',
    },
    mailSend: {
        keys: keysOf({
            to: true,
            subject: true,
            body: true,
            itemInstanceIds: true,
            shards: true,
            codAmount: true,
        }),
        check: validateMailSend,
        error: 'badMailSend',
    },
    mailClaim: {
        keys: keysOf({ mailId: true }),
        check: validateMailClaim,
        error: 'badMailClaim',
    },
    mailReturn: {
        keys: keysOf({ mailId: true }),
        check: validateMailReturn,
        error: 'badMailReturn',
    },
    mailList: { keys: keysOf({}), check: ACCEPT, error: 'badMailList' },
    guildCreate: {
        keys: keysOf({ name: true, tag: true }),
        check: validateGuildCreate,
        error: 'badGuildCreate',
    },
    guildInvite: {
        keys: keysOf({ name: true }),
        check: validateGuildInvite,
        error: 'badGuildInvite',
    },
    guildInviteRespond: {
        keys: keysOf({ guildId: true, accept: true }),
        check: validateGuildInviteRespond,
        error: 'badGuildInviteRespond',
    },
    guildKick: {
        keys: keysOf({ accountId: true }),
        check: validateGuildKick,
        error: 'badGuildKick',
    },
    guildLeave: { keys: keysOf({}), check: ACCEPT, error: 'badGuildLeave' },
    guildPromote: {
        keys: keysOf({ accountId: true }),
        check: validateGuildPromote,
        error: 'badGuildPromote',
    },
    guildDemote: {
        keys: keysOf({ accountId: true }),
        check: validateGuildDemote,
        error: 'badGuildDemote',
    },
    guildSetMotd: {
        keys: keysOf({ motd: true }),
        check: validateGuildSetMotd,
        error: 'badGuildSetMotd',
    },
    guildTransfer: {
        keys: keysOf({ accountId: true }),
        check: validateGuildTransfer,
        error: 'badGuildTransfer',
    },
    guildDisband: { keys: keysOf({}), check: ACCEPT, error: 'badGuildDisband' },
    guildRequest: { keys: keysOf({}), check: ACCEPT, error: 'badGuildRequest' },
    houseAcquire: {
        keys: keysOf({ templateId: true, name: true }),
        check: validateHouseAcquire,
        error: 'badHouseAcquire',
    },
    houseEnter: {
        keys: keysOf({ houseId: true }),
        check: validateHouseEnter,
        error: 'badHouseEnter',
    },
    houseLeave: { keys: keysOf({}), check: ACCEPT, error: 'badHouseLeave' },
    housePlaceDecoration: {
        keys: keysOf({ houseId: true, itemInstanceId: true, slot: true }),
        check: validateHousePlaceDecoration,
        error: 'badHousePlaceDecoration',
    },
    houseRemoveDecoration: {
        keys: keysOf({ houseId: true, decorationId: true }),
        check: validateHouseRemoveDecoration,
        error: 'badHouseRemoveDecoration',
    },
    housePlaceTrophy: {
        keys: keysOf({ houseId: true, entitlementId: true, slot: true }),
        check: validateHousePlaceTrophy,
        error: 'badHousePlaceTrophy',
    },
    houseRemoveTrophy: {
        keys: keysOf({ houseId: true, decorationId: true }),
        check: validateHouseRemoveTrophy,
        error: 'badHouseRemoveTrophy',
    },
    houseSetPermissions: {
        keys: keysOf({
            houseId: true,
            policy: true,
            allow: true,
            ban: true,
        }),
        check: validateHouseSetPermissions,
        error: 'badHouseSetPermissions',
    },
    houseSetName: {
        keys: keysOf({ houseId: true, name: true }),
        check: validateHouseSetName,
        error: 'badHouseSetName',
    },
    housePayUpkeep: {
        keys: keysOf({ houseId: true }),
        check: validateHousePayUpkeep,
        error: 'badHousePayUpkeep',
    },
    houseList: { keys: keysOf({}), check: ACCEPT, error: 'badHouseList' },
    workshopQueueJob: {
        keys: keysOf({
            houseId: true,
            recipeId: true,
            inputs: true,
            quantity: true,
        }),
        check: validateWorkshopQueueJob,
        error: 'badWorkshopQueueJob',
    },
    workshopCollect: {
        keys: keysOf({ jobId: true }),
        check: validateWorkshopCollect,
        error: 'badWorkshopCollect',
    },
    workshopCancel: {
        keys: keysOf({ jobId: true }),
        check: validateWorkshopCancel,
        error: 'badWorkshopCancel',
    },
    workshopList: { keys: keysOf({}), check: ACCEPT, error: 'badWorkshopList' },
    arenaQueue: {
        keys: keysOf({ format: true }),
        check: validateArenaQueue,
        error: 'badArenaQueue',
    },
    arenaLeave: { keys: keysOf({}), check: ACCEPT, error: 'badArenaLeave' },
    arenaState: { keys: keysOf({}), check: ACCEPT, error: 'badArenaState' },
    arenaLeaderboard: {
        keys: keysOf({ format: true, limit: true }),
        check: validateArenaLeaderboard,
        error: 'badArenaLeaderboard',
    },
    raidForm: {
        keys: keysOf({ encounterId: true, members: true }),
        check: validateRaidForm,
        error: 'badRaidForm',
    },
    raidLeave: { keys: keysOf({}), check: ACCEPT, error: 'badRaidLeave' },
    raidState: { keys: keysOf({}), check: ACCEPT, error: 'badRaidState' },
    raidInvite: {
        keys: keysOf({ encounterId: true, memberAccountId: true }),
        check: validateRaidInvite,
        error: 'badRaidInvite',
    },
    raidAccept: {
        keys: keysOf({ invitationId: true }),
        check: validateRaidAccept,
        error: 'badRaidAccept',
    },
    seasonState: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badSeasonState',
    },
    vendorList: {
        keys: keysOf({ npcId: true }),
        check: validateVendorList,
        error: 'badVendorList',
    },
    repair: {
        keys: keysOf({ npcId: true, itemInstanceId: true }),
        check: validateRepair,
        error: 'badRepair',
    },
    vendorSell: {
        keys: keysOf({ npcId: true, itemInstanceId: true }),
        check: validateVendorSell,
        error: 'badVendorSell',
    },
    travel: {
        keys: keysOf({ shrineId: true }),
        check: validateTravel,
        error: 'badTravel',
    },
    capacityUpgrade: {
        keys: keysOf({
            npcId: true,
            cap: true,
            tier: true,
            containerId: true,
        }),
        check: validateCapacityUpgrade,
        error: 'badCapacityUpgrade',
    },
    dungeonEnter: {
        keys: keysOf({ dungeonId: true }),
        check: validateDungeonEnter,
        error: 'badDungeonEnter',
    },
    dungeonLeave: { keys: keysOf({}), check: ACCEPT, error: 'badDungeonLeave' },
    respawnIntent: {
        keys: keysOf({}),
        check: ACCEPT,
        error: 'badRespawnIntent',
    },
    hintEvent: {
        keys: keysOf({ hintId: true, event: true }),
        check: validateHintEvent,
        error: 'badHintEvent',
    },
};
/** The client → server message-type set, derived from the shape table (one per union member). */
export const CLIENT_MESSAGE_TYPES = Object.freeze(Object.keys(CLIENT_MESSAGE_SHAPES));
/** The envelope carries exactly these keys (networking.md "Encoding And Envelope"). */
const ENVELOPE_KEYS = Object.freeze(['v', 't', 's', 'd']);
/**
 * Parse and shape-validate one raw frame from the socket.
 * Never throws: every failure is a typed rejection the Gateway can count.
 */
export function parseClientMessage(raw) {
    if (raw.length > MAX_CLIENT_MESSAGE_BYTES) {
        return { ok: false, error: 'messageTooLarge' };
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return { ok: false, error: 'invalidJson' };
    }
    if (!isRecord(parsed))
        return { ok: false, error: 'notAnObject' };
    const { v, t, s, d } = parsed;
    if (v !== PROTOCOL_VERSION)
        return { ok: false, error: 'protocolMismatch' };
    if (!isNonNegativeInt(s))
        return { ok: false, error: 'badSequence' };
    if (!isRecord(d))
        return { ok: false, error: 'badPayload' };
    // Strict shape (D-5): the envelope is exactly { v, t, s, d } …
    if (!hasOnlyKeys(parsed, ENVELOPE_KEYS))
        return { ok: false, error: 'unexpectedField' };
    if (typeof t !== 'string' || !Object.prototype.hasOwnProperty.call(CLIENT_MESSAGE_SHAPES, t)) {
        return { ok: false, error: 'unknownType' };
    }
    const shape = CLIENT_MESSAGE_SHAPES[t];
    // … and the payload exactly its declared keys — checked BEFORE the field
    // validator, so the rejection names the cause (`unexpectedField`) rather than
    // reporting a well-formed payload as `bad<Type>`.
    if (!hasOnlyKeys(d, shape.keys))
        return { ok: false, error: 'unexpectedField' };
    if (!shape.check(d))
        return { ok: false, error: shape.error };
    return { ok: true, msg: { v, t, s, d } };
}
//# sourceMappingURL=validate.js.map